import React from 'react';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, cx } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { CardAvatarFrame } from './CardAvatarFrame';
import { CardCover as LegacyCardCover } from './Legacy/CardCover';

export type CardCoverStyle = NonNullable<CardCoverProps['coverStyle']>;
export type CardCoverProps = React.ComponentProps<typeof LegacyCardCover> & {
	coverImageUrl?: string;
};

import { CardCoverArtwork } from './CardCoverArtwork';

const styles = cssMap({
	cover: {
		gridArea: 'cover',
		position: 'relative',
		display: 'grid',
		placeItems: 'center',
		rowGap: token('space.200'),
		paddingBlockEnd: token('space.300'),
	},
	background: {
		position: 'absolute',
		insetBlockStart: token('space.negative.200'),
		insetBlockEnd: token('space.0'),
		insetInlineStart: token('space.negative.200'),
		insetInlineEnd: token('space.negative.200'),
		pointerEvents: 'none',
	},
	neutral: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	image: {
		position: 'absolute',
		insetBlockStart: token('space.0'),
		insetInlineStart: token('space.0'),
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	gradient: {
		maskImage: 'linear-gradient(to bottom, black, transparent)',
	},
	cutout: {
		position: 'relative',
		boxSizing: 'border-box',
		marginBlockStart: token('space.negative.150'),
		width: '40px',
		height: '8px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.full'),
	},
	avatar: {
		position: 'relative',
		display: 'flex',
	},
	trailing: {
		position: 'absolute',
		insetBlockStart: token('space.0'),
		insetInlineEnd: token('space.0'),
		zIndex: 200,
		display: 'grid',
		justifyItems: 'end',
		alignItems: 'center',
		maxWidth: '100%',
	},
	layer: { gridArea: '1 / 1' },
});

const DefaultAvatar = ({ name, iconUrl }: { name: string; iconUrl?: string }): React.JSX.Element =>
	iconUrl ? (
		<Image src={iconUrl} alt={name} width={48} height={48} />
	) : (
		<CardAvatarFrame>
			<Avatar name={name} size="xlarge" />
		</CardAvatarFrame>
	);

export const CardCover = (props: CardCoverProps): React.JSX.Element => {
	if (!fg('rovo_agents_studio_browse_kit_uplift')) {
		return <LegacyCardCover {...props} />;
	}

	const {
		name,
		avatar: AvatarComponent = DefaultAvatar,
		coverStyle = 'grid',
		coverColor,
		coverImageUrl,
		children,
		badge,
		iconUrl,
		testId,
	} = props;

	return (
		<Box xcss={styles.cover} testId={testId}>
			{fg('rovo_agents_studio_browse_kit_cover_image') && coverImageUrl ? (
				<Box
					xcss={cx(styles.background, styles.neutral)}
					style={{ backgroundColor: coverColor }}
					aria-hidden="true"
				>
					<Box as="img" alt="" src={coverImageUrl} xcss={styles.image} />
				</Box>
			) : coverStyle !== 'gradient' ? (
				<Box xcss={cx(styles.background, styles.neutral)} aria-hidden="true">
					{coverStyle === 'grid' ? <CardCoverArtwork /> : null}
				</Box>
			) : (
				<Box
					xcss={cx(styles.background, styles.gradient)}
					style={{ backgroundColor: coverColor }}
					aria-hidden="true"
				/>
			)}
			<Box xcss={styles.cutout} data-card-cutout aria-hidden="true" />
			<Box xcss={styles.avatar}>
				<AvatarComponent name={name} iconUrl={iconUrl} />
			</Box>
			{badge || children ? (
				<Box xcss={styles.trailing}>
					{badge ? <Box xcss={styles.layer}>{badge}</Box> : null}
					{children ? <Box xcss={styles.layer}>{children}</Box> : null}
				</Box>
			) : null}
		</Box>
	);
};
